/**
 * Where a visit starts. Story VC-016.
 *
 * Two ways in and a list of conversations to go back to. The two entries are
 * deliberately the same size: a caller who wants to talk and one who wants
 * to type are not a primary and a secondary action, and making one smaller
 * answers a question nobody asked.
 *
 * The list is this browser's, not the tenant's — `agent_runtime_lld.md` §40
 * has no endpoint that lists sessions, and inventing one here would be a
 * client deciding a retention policy. The screen says so rather than leaving
 * a user to discover it by opening a second device.
 *
 * Picking one up continues it *by typing*, whichever way it started, and the
 * row says so. `voice_wire_protocol.md` §10 resumes a voice session only
 * inside a fifteen-second grace window and is explicit about what happens
 * after it: "the runtime session and its conversation survive, so a consumer
 * can offer continuity at the application level; the protocol does not do it
 * implicitly." A row that silently started a *new* call would be the thing
 * this list exists to avoid.
 */

import { useState } from "react";

import { Icon, Mark } from "../components/Icon.jsx";
import {
  describeWhen,
  forgetSession,
  loadSessions,
} from "../session/store.js";

/**
 * @param {{ onStart: (kind: "call" | "chat") => void,
 *           onResume: (session: any) => void,
 *           onInspect: (sessionId: string) => void,
 *           agent: string, callBlocked: string | null }} props
 */
export function Home({ onStart, onResume, onInspect, agent, callBlocked }) {
  const [sessions, setSessions] = useState(() => loadSessions());

  return (
    <div className="home">
      <header className="home__hero">
        <div className="home__mark">
          <Mark size={44} />
          <span className="appbar__subtitle">{agent}</span>
        </div>
        <h1 className="home__greeting">How can we help?</h1>
        <p className="home__lede">
          Speak to the clinic's assistant or type instead. Either way it is
          the same conversation, and it remembers what you said in the other.
        </p>
      </header>

      <div className="home__actions">
        <button
          type="button"
          className="entry entry--call"
          onClick={() => onStart("call")}
          disabled={!!callBlocked}
        >
          <span className="entry__face">
            <Icon name="call" size={28} />
          </span>
          <span>
            <span className="entry__title">Call AI Agent</span>
            <span className="entry__detail">
              {callBlocked ?? "Talk out loud. You can interrupt at any time."}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="entry entry--chat"
          onClick={() => onStart("chat")}
        >
          <span className="entry__face">
            <Icon name="chat" size={28} />
          </span>
          <span>
            <span className="entry__title">Chat with Agent</span>
            <span className="entry__detail">
              Type instead. No microphone needed.
            </span>
          </span>
        </button>
      </div>

      <section className="home__section">
        <div className="home__section-head">
          <h2 className="home__section-title">
            <Icon name="history" size={14} /> Continue where you left off
          </h2>
        </div>

        {sessions.length === 0 ? (
          <p className="empty">
            Nothing yet. Conversations you start here will be listed so you
            can pick one up — on this device, because the runtime has no
            endpoint that lists a tenant's sessions.
          </p>
        ) : (
          sessions.map((session) => (
            <div className="card card--interactive" key={session.id}>
              <div className="session-row">
                <button
                  type="button"
                  className="session-row__main"
                  onClick={() => onResume(session)}
                >
                  <span className="session-row__face">
                    <Icon
                      name={session.kind === "call" ? "call" : "chat"}
                      size={18}
                    />
                  </span>
                  <span className="session-row__body">
                    <span className="session-row__title">
                      {session.title || "A conversation with no first line"}
                    </span>
                    <span className="session-row__meta">
                      {session.kind === "call" ? "call" : "chat"} ·{" "}
                      {describeWhen(session.updatedAt)}
                      {session.ended
                        ? " · ended"
                        : session.kind === "call"
                          ? " · continue by typing"
                          : ""}
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => onInspect(session.id)}
                  aria-label={`Open the debug panel for ${
                    session.title || "this session"
                  }`}
                  title="What happened inside this session"
                >
                  <Icon name="pulse" size={20} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setSessions(forgetSession(session.id))}
                  aria-label={`Remove ${
                    session.title || "this session"
                  } from this device`}
                  title="Remove from this device"
                >
                  <Icon name="trash" size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
